// client/src/components/stats/benchmarks.ts
//
// Tipos y lógica común para todos los benchmarks de la app.
//
// Este módulo está diseñado para soportar dos fuentes de benchmarks:
//
// 1. THEORETICAL — los hardcoded actuales, basados en consensus de solver
//    studies y coaching content moderno. Son aproximados y dependen del meta.
//
// 2. POPULATION — calculados dinámicamente desde la base de datos cuando
//    haya suficientes usuarios opted-in al sharing. Más +EV de seguir
//    porque reflejan el meta real del stake/formato del user.
//
// La UI no debería saber qué fuente está usando — solo recibe { min, max,
// source } y muestra el rango. Cuando agreguemos population benchmarks,
// el switch va a ser un cambio de un solo lugar (un fetch en lugar del
// hardcoded), sin tocar componentes.

export type BenchmarkSource = 'theoretical' | 'population';

export interface Benchmark {
  min: number;
  max: number;
  /** De dónde vino este rango. La UI puede mostrarlo distinto según source. */
  source: BenchmarkSource;
  /** Solo poblado si source === 'population'. Number of users in the pool. */
  sampleSize?: number;
  /** Solo poblado si source === 'population'. ISO date string. */
  lastUpdated?: string;
}

export type BenchmarkStatus = 'ok' | 'warn' | 'critical' | 'no-benchmark';

/**
 * Evaluación común contra un benchmark.
 *
 * - 'ok': dentro del rango
 * - 'warn': fuera del rango por menos de 1.5x el ancho del rango
 * - 'critical': fuera por más de 1.5x el ancho del rango
 * - 'no-benchmark': no hay benchmark disponible (NO es leak, solo info)
 *
 * Esta es la misma lógica que `evalStat` y `evalRfi` ya usan — la centralizamos
 * para que cualquier nueva stat use exactamente el mismo criterio.
 */
export function evalBenchmark(value: number, benchmark: Benchmark | null): BenchmarkStatus {
  if (!benchmark) return 'no-benchmark';
  if (value >= benchmark.min && value <= benchmark.max) return 'ok';
  const gap = value > benchmark.max ? value - benchmark.max : benchmark.min - value;
  const range = benchmark.max - benchmark.min || 5;
  return gap > range * 1.5 ? 'critical' : 'warn';
}

/**
 * Sample mínimo para que tenga sentido evaluar contra un benchmark.
 *
 * Por debajo de este sample, mostramos el número observado pero NO la
 * evaluación contra el rango (sin dot rojo/amarillo/verde). Razón: el
 * ruido estadístico de un sample chico es comparable al ancho del rango,
 * así que cualquier evaluación es más ruido que señal.
 *
 * Threshold elegido (50): con n=50 el IC al 95% de un porcentaje es
 * aproximadamente ±14%, que es similar al ancho típico de un benchmark
 * de matchup. Por debajo de eso, evaluar es engañoso.
 */
export const MIN_SAMPLE_FOR_BENCHMARK_EVAL = 50;
